/**
 * useRecurring Hook - Detects recurring income and expense patterns
 *
 * IMPROVED Detection Logic (v2):
 * - Pay Cycles: Filters by amount ($500+), excludes adjustments/loans/transfers
 * - Bills: Requires 3+ occurrences, checks interval consistency, variance limits
 * - Smarter grouping: Preserves key identifiers in descriptions
 *
 * Performance: O(n) where n = transactions, memoized results
 */

import { useMemo, useState, useEffect, useRef } from 'react';
import { db, Transaction } from '@services/db';
import { dataEvents } from '@services/dataEvents';
import { bridgeAllDetected } from '@services/scheduledBridge';
import { syncManager } from '@services/syncManager';

// ============================================================
// Types
// ============================================================

export interface DetectedIncome {
  id: string;
  name: string;
  schedule: string;
  averageAmount: number;
  icon: string;
  isEstimate: boolean;
  occurrences: number;
  lastDate: Date;
}

export interface DetectedBill {
  id: string;
  name: string;
  account: string;
  averageAmount: number;
  dayOfMonth: string;
  isEstimate: boolean;
  occurrences: number;
  lastDate: Date;
}

export interface RecurringData {
  /** Top income items for UI display */
  income: DetectedIncome[];
  /** Top bill items for UI display */
  bills: DetectedBill[];
  /** ALL detected income (for bridging to schedules) */
  allIncome: DetectedIncome[];
  /** ALL detected bills (for bridging to schedules) */
  allBills: DetectedBill[];
  totalMonthlyIncome: number;
  totalMonthlyBills: number;
  loading: boolean;
}

// ============================================================
// Constants
// ============================================================

// Keywords that indicate this is NOT salary/pay
const INCOME_EXCLUDE_KEYWORDS = [
  'adjustment', 'adjust', 'fix', 'correction', 'correct',
  'loan', 'transfer', 'xfer', 'refund', 'cashback', 'cash back',
  'reimbursement', 'reimburse', 'credit', 'bonus', 'one-time',
  'gift', 'rebate', 'return', 'reversal',
];

// BUG-053 FIX: Keywords that indicate this is NOT a recurring bill
// These are merchants where frequent purchases are normal but not "bills"
//
// RECURRING-FIX: Issue 1 - Added donations/charities/religious/crowdfunding
// NOTE: Avoid single word "church" to not exclude "church's chicken"
const BILL_EXCLUDE_KEYWORDS = [
  // Food delivery - variable purchases, not bills
  'doordash', 'door dash', 'uber eats', 'ubereats', 'grubhub',
  'postmates', 'instacart', 'seamless', 'caviar', 'gopuff',
  // Shopping - variable purchases, not bills
  'amazon', 'amzn', 'prime now', 'whole foods',
  'target', 'walmart', 'costco', 'sam\'s club', 'sams club',
  'walgreens', 'cvs', 'rite aid',
  // Rideshare - variable, not bills
  'lyft', 'uber trip', 'uber ride',
  // Cafes - too frequent to be "bills"
  'starbucks', 'dunkin', 'coffee', 'cafe', 'peets',
  // Groceries - variable purchases
  'grocery', 'kroger', 'safeway', 'publix', 'trader joe',
  'aldi', 'wegmans', 'sprouts', 'food lion', 'piggly',
  // Fast food - too variable
  'mcdonald', 'burger king', 'wendy', 'taco bell', 'chipotle',
  'chick-fil-a', 'chickfila', 'subway', 'panda express',
  // Gas - variable purchases
  'shell', 'chevron', 'exxon', 'mobil', 'bp ', 'gas station',
  'speedway', 'circle k', 'wawa', 'sheetz',
  // ATM/Cash - not bills
  'atm', 'cash withdraw', 'cash back',
  // General retail
  'best buy', 'home depot', 'lowes', 'ikea', 'bed bath',
  // RECURRING-FIX: Issue 1 - Donations & charities (variable, not bills)
  'donation', 'donate', 'donating', 'donor', 'contrib', 'giving',
  'charity', 'nonprofit', 'non-profit', 'npo', '501c',
  'foundation', 'aclu', 'red cross', 'unicef', 'npr', 'pbs',
  'planned parenthood', 'world vision', 'habitat for humanity',
  'united way', 'salvation army', 'goodwill', 'doctors without',
  'msf', 'sierra club', 'wwf', 'nature conserv', 'humane society',
  // Crowdfunding - one-time or variable pledges
  'gofundme', 'patreon', 'kickstarter', 'indiegogo', 'ko-fi',
  'buy me a coffee', 'opencollective',
  // Religious - variable tithing (using multi-word to avoid "church's chicken")
  'tithe', 'tithing', 'offering', 'temple donation',
  'church donation', 'church tithe', 'synagogue', 'mosque',
  // Political - campaign contributions (variable)
  'actblue', 'winred', 'campaign contrib', 'pac contrib', 'political',
];

// Keywords that suggest this IS salary/pay
const SALARY_KEYWORDS = [
  'salary', 'payroll', 'paycheck', 'pay check', 'wage', 'wages',
  'direct deposit', 'dd', 'employer', 'work', 'job',
];

// Minimum amount to consider as potential salary (biweekly)
const MIN_SALARY_AMOUNT = 500;

// Minimum occurrences for bills to be considered recurring
// 2 if same day of month (strong signal), 3 otherwise
const MIN_BILL_OCCURRENCES_STRICT = 3;
const MIN_BILL_OCCURRENCES_SAME_DAY = 2;

// Maximum coefficient of variation for bill amounts (0.3 = 30% variance)
const MAX_BILL_AMOUNT_VARIANCE = 0.35;

// ============================================================
// Helpers
// ============================================================

/**
 * Normalize description for grouping - IMPROVED.
 * Preserves key identifiers, removes only truly irrelevant characters.
 * Returns '__INCOME_BY_AMOUNT__' for empty income descriptions to allow amount-based grouping.
 */
function normalizeDescription(desc: string, type?: string, amount?: number): string {
  const normalized = desc
    .toLowerCase()
    .replace(/[#*@]+/g, '')
    .replace(/\s+/g, ' ')
    .trim();

  // For income with no description, use a single group key (will detect by schedule)
  if (!normalized && type === 'income' && amount && amount >= MIN_SALARY_AMOUNT) {
    return '__salary_no_desc__';
  }

  return normalized;
}

/**
 * Check if description likely represents salary/paycheck.
 * Also accepts empty descriptions if amount is salary-like.
 */
function isSalaryLike(desc: string, amount: number): boolean {
  const lower = desc.toLowerCase();

  // Must meet minimum amount threshold
  if (amount < MIN_SALARY_AMOUNT) {
    return false;
  }

  // Empty description but salary-like amount - accept it
  if (!lower.trim()) {
    return true;
  }

  // Exclude if contains exclusion keywords
  if (INCOME_EXCLUDE_KEYWORDS.some(kw => lower.includes(kw))) {
    return false;
  }

  // Accept if has salary keyword OR meets amount threshold
  return true;
}

/**
 * Calculate coefficient of variation (std dev / mean).
 * Lower = more consistent amounts.
 */
function calcVariance(amounts: number[]): number {
  if (amounts.length < 2) return 0;
  const mean = amounts.reduce((a, b) => a + b, 0) / amounts.length;
  if (mean === 0) return 0;
  const squaredDiffs = amounts.map(a => Math.pow(a - mean, 2));
  const variance = squaredDiffs.reduce((a, b) => a + b, 0) / amounts.length;
  const stdDev = Math.sqrt(variance);
  return stdDev / mean;
}

/**
 * Check if intervals suggest monthly pattern (25-35 day average).
 */
function isMonthlyPattern(dates: Date[]): boolean {
  if (dates.length < 2) return false;

  const sorted = [...dates].sort((a, b) => a.getTime() - b.getTime());
  const intervals: number[] = [];

  for (let i = 1; i < sorted.length; i++) {
    const diff = Math.round(
      (sorted[i].getTime() - sorted[i - 1].getTime()) / (1000 * 60 * 60 * 24)
    );
    intervals.push(diff);
  }

  const avgInterval = intervals.reduce((a, b) => a + b, 0) / intervals.length;

  // Monthly pattern: average interval between 20 and 40 days
  return avgInterval >= 20 && avgInterval <= 40;
}

/**
 * Detect pay schedule from dates.
 */
function detectSchedule(dates: Date[]): string {
  if (dates.length < 2) return 'One-time';

  const sorted = [...dates].sort((a, b) => a.getTime() - b.getTime());
  const days = sorted.map((d) => d.getDate());
  const uniqueDays = [...new Set(days)];

  // Check for 1st and 15th pattern (semi-monthly)
  const has1st = days.some(d => d <= 3);
  const has15th = days.some(d => d >= 13 && d <= 17);
  if (has1st && has15th && uniqueDays.length >= 2) {
    return 'Every 1st & 15th';
  }

  // Calculate intervals
  const intervals: number[] = [];
  for (let i = 1; i < sorted.length; i++) {
    const diff = Math.round(
      (sorted[i].getTime() - sorted[i - 1].getTime()) / (1000 * 60 * 60 * 24)
    );
    intervals.push(diff);
  }

  const avgInterval = intervals.reduce((a, b) => a + b, 0) / intervals.length;

  if (avgInterval >= 12 && avgInterval <= 16) {
    return 'Every 2 weeks';
  }

  if (avgInterval >= 25 && avgInterval <= 35) {
    const avgDay = Math.round(days.reduce((a, b) => a + b, 0) / days.length);
    return `Monthly (${ordinal(avgDay)})`;
  }

  if (avgInterval >= 6 && avgInterval <= 8) {
    return 'Weekly';
  }

  return 'Irregular';
}

/**
 * Get ordinal suffix (1st, 2nd, 3rd, etc.)
 */
function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

/**
 * Get day of month string for bills - with outlier detection.
 * Uses mode (most common) or median to handle duplicate/error entries.
 */
function getDayOfMonth(dates: Date[]): string {
  if (dates.length === 0) return 'Various';

  const days = dates.map((d) => d.getDate());

  // Find mode (most common day)
  const dayCounts = new Map<number, number>();
  for (const day of days) {
    dayCounts.set(day, (dayCounts.get(day) || 0) + 1);
  }

  // Sort by frequency
  const sortedByFreq = [...dayCounts.entries()].sort((a, b) => b[1] - a[1]);

  // If one day appears significantly more often, use it (handles duplicates)
  if (sortedByFreq.length > 0) {
    const [mostCommonDay, mostCommonCount] = sortedByFreq[0];
    const totalCount = days.length;

    // If most common day appears in 50%+ of transactions, use it alone
    if (mostCommonCount >= totalCount * 0.5) {
      return ordinal(mostCommonDay);
    }
  }

  // Otherwise, use range logic
  const uniqueDays = [...new Set(days)].sort((a, b) => a - b);

  if (uniqueDays.length === 1) {
    return ordinal(uniqueDays[0]);
  }

  // Check spread - if days are close together, show range
  const min = Math.min(...uniqueDays);
  const max = Math.max(...uniqueDays);

  if (max - min <= 10) {
    return `${ordinal(min)}-${ordinal(max)}`;
  }

  if (uniqueDays.length <= 2) {
    return uniqueDays.map(ordinal).join(', ');
  }

  return 'Various';
}

/**
 * Detect icon based on description.
 */
function detectIcon(desc: string, type: 'income' | 'expense'): string {
  const lower = desc.toLowerCase();

  if (type === 'income') {
    if (SALARY_KEYWORDS.some(kw => lower.includes(kw))) {
      return '💼';
    }
    if (lower.includes('freelance') || lower.includes('contract')) {
      return '💻';
    }
    if (lower.includes('dividend') || lower.includes('investment')) {
      return '📈';
    }
    return '💰';
  }

  // Expense icons
  if (lower.includes('rent') || lower.includes('mortgage')) return '🏠';
  if (lower.includes('electric') || lower.includes('power')) return '⚡';
  if (lower.includes('gas') || lower.includes('utility')) return '🔥';
  if (lower.includes('water')) return '💧';
  if (lower.includes('internet') || lower.includes('wifi')) return '📶';
  if (lower.includes('phone') || lower.includes('mobile')) return '📱';
  if (lower.includes('insurance')) return '🛡️';
  if (lower.includes('car') || lower.includes('auto') || lower.includes('malibu')) return '🚗';
  if (lower.includes('netflix') || lower.includes('hulu') || lower.includes('disney')) return '📺';
  if (lower.includes('spotify') || lower.includes('music')) return '🎵';
  if (lower.includes('gym') || lower.includes('fitness')) return '💪';
  if (lower.includes('subscription') || lower.includes('google ads')) return '📦';
  return '📋';
}

/**
 * Capitalize name properly.
 */
function formatName(name: string): string {
  return name
    .split(' ')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ')
    .substring(0, 30);
}

// ============================================================
// Hook
// ============================================================

export function useRecurring(monthsToAnalyze = 6): RecurringData {
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState<Transaction[]>([]);

  // Fetch transactions
  useEffect(() => {
    async function fetchTransactions() {
      setLoading(true);
      try {
        const now = new Date();
        const start = new Date(now.getFullYear(), now.getMonth() - monthsToAnalyze, 1);

        const all = await db.transactions.toArray();
        const filtered = all.filter((t) => {
          if (t.deletedAt) return false;
          const date = new Date(t.date);
          return date >= start && date <= now;
        });

        setTransactions(filtered);
      } catch (error) {
        console.error('[useRecurring] Failed to fetch:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchTransactions();

    const unsub = dataEvents.on('transactions', fetchTransactions);
    return () => unsub();
  }, [monthsToAnalyze]);

  // Analyze transactions for patterns
  const result = useMemo((): Omit<RecurringData, 'loading'> => {
    if (transactions.length === 0) {
      return {
        income: [],
        bills: [],
        allIncome: [],
        allBills: [],
        totalMonthlyIncome: 0,
        totalMonthlyBills: 0,
      };
    }

    // Group by normalized description
    const incomeGroups = new Map<
      string,
      { transactions: Transaction[]; originalName: string }
    >();
    const expenseGroups = new Map<
      string,
      { transactions: Transaction[]; originalName: string; accountId: string }
    >();

    for (const t of transactions) {
      if (t.type === 'income') {
        // PRE-FILTER: Skip income that doesn't look like salary
        if (!isSalaryLike(t.description, t.amount)) {
          continue;
        }

        // Use amount-based grouping for empty descriptions
        const key = normalizeDescription(t.description, 'income', t.amount);
        if (!key) continue;

        const existing = incomeGroups.get(key);
        if (existing) {
          existing.transactions.push(t);
        } else {
          // For empty descriptions, use "Salary" as name
          const displayName = t.description.trim() || 'Salary';
          incomeGroups.set(key, { transactions: [t], originalName: displayName });
        }
      } else if (t.type === 'expense') {
        const key = normalizeDescription(t.description);
        if (!key || key.length < 2) continue;

        // BUG-053 FIX: Skip merchants that are known false positives for recurring bills
        const lowerDesc = t.description.toLowerCase();
        if (BILL_EXCLUDE_KEYWORDS.some(kw => lowerDesc.includes(kw))) {
          continue;
        }

        const existing = expenseGroups.get(key);
        if (existing) {
          existing.transactions.push(t);
        } else {
          expenseGroups.set(key, {
            transactions: [t],
            originalName: t.description,
            accountId: t.accountId,
          });
        }
      }
    }

    // Detect recurring income (salary/pay)
    const detectedIncome: DetectedIncome[] = [];
    for (const [key, group] of incomeGroups) {
      // Require 2+ occurrences for income
      if (group.transactions.length < 2) continue;

      const amounts = group.transactions.map((t) => t.amount);
      const avgAmount = amounts.reduce((a, b) => a + b, 0) / amounts.length;

      // Relaxed variance for salary (overtime can cause high variance)
      // Be very lenient for no-description salary group
      const variance = calcVariance(amounts);
      const isNoDescSalary = key === '__salary_no_desc__';
      const maxVariance = isNoDescSalary ? 1.0 : 0.6; // 100% for no-desc (overtime), 60% for named
      if (variance > maxVariance) continue;

      const dates = group.transactions.map((t) => new Date(t.date));
      const schedule = detectSchedule(dates);

      // Skip irregular patterns for salary (but keep if we have 4+ occurrences - likely real)
      if ((schedule === 'Irregular' || schedule === 'One-time') && group.transactions.length < 4) continue;

      const isEstimate = new Set(amounts).size > 1;
      const name = formatName(group.originalName);

      detectedIncome.push({
        id: `income-${name.replace(/\s/g, '-')}`,
        name,
        schedule,
        averageAmount: Math.round(avgAmount),
        icon: detectIcon(name, 'income'),
        isEstimate,
        occurrences: group.transactions.length,
        lastDate: dates.sort((a, b) => b.getTime() - a.getTime())[0],
      });
    }

    // Detect recurring bills
    const detectedBills: DetectedBill[] = [];
    for (const [_key, group] of expenseGroups) {
      const dates = group.transactions.map((t) => new Date(t.date));
      const days = dates.map(d => d.getDate());

      // Check if all payments fall on same day of month (within ±2 days)
      const minDay = Math.min(...days);
      const maxDay = Math.max(...days);
      const isSameDayPattern = (maxDay - minDay) <= 4;

      // Smart threshold: 2 if same day pattern, 3 otherwise
      const minOccurrences = isSameDayPattern
        ? MIN_BILL_OCCURRENCES_SAME_DAY
        : MIN_BILL_OCCURRENCES_STRICT;

      if (group.transactions.length < minOccurrences) {
        continue;
      }

      const amounts = group.transactions.map((t) => t.amount);
      const avgAmount = amounts.reduce((a, b) => a + b, 0) / amounts.length;

      // Skip if amount variance is too high (not a consistent bill)
      const variance = calcVariance(amounts);
      if (variance > MAX_BILL_AMOUNT_VARIANCE) {
        continue;
      }

      // Must have monthly-ish pattern (dates already computed above)
      if (!isMonthlyPattern(dates)) {
        continue;
      }

      const dayOfMonth = getDayOfMonth(dates);
      const isEstimate = new Set(amounts).size > 1;
      const name = formatName(group.originalName);

      detectedBills.push({
        id: `bill-${name.replace(/\s/g, '-')}`,
        name,
        account: group.accountId,
        averageAmount: Math.round(avgAmount),
        dayOfMonth,
        isEstimate,
        occurrences: group.transactions.length,
        lastDate: dates.sort((a, b) => b.getTime() - a.getTime())[0],
      });
    }

    // Sort by amount (highest first)
    detectedIncome.sort((a, b) => b.averageAmount - a.averageAmount);
    detectedBills.sort((a, b) => b.averageAmount - a.averageAmount);

    // Calculate monthly totals
    const totalMonthlyIncome = detectedIncome.reduce((sum, inc) => {
      if (inc.schedule.includes('1st & 15th')) {
        return sum + inc.averageAmount * 2;
      }
      if (inc.schedule.includes('2 weeks')) {
        return sum + inc.averageAmount * 2.17;
      }
      if (inc.schedule.includes('Weekly')) {
        return sum + inc.averageAmount * 4.33;
      }
      return sum + inc.averageAmount;
    }, 0);

    const totalMonthlyBills = detectedBills.reduce((sum, bill) => {
      return sum + bill.averageAmount;
    }, 0);

    return {
      // Full arrays for bridging (schedules ALL detected patterns)
      allIncome: detectedIncome,
      allBills: detectedBills,
      // Sliced arrays for UI display (top items only)
      income: detectedIncome.slice(0, 10),
      bills: detectedBills.slice(0, 15),
      totalMonthlyIncome: Math.round(totalMonthlyIncome),
      totalMonthlyBills: Math.round(totalMonthlyBills),
    };
  }, [transactions]);

  // Track bridging to prevent duplicate calls
  const resultSignature = useRef('');

  // Auto-bridge detected patterns to scheduled transactions
  // Use FULL arrays (allIncome, allBills) not the sliced UI arrays
  //
  // RECURRING-FIX: Issue 5C - Defers bridging while sync is in progress
  // This prevents recreating schedules before deletions sync from other devices
  useEffect(() => {
    // Create a signature from the result to detect meaningful changes
    const signature = JSON.stringify({
      incomeCount: result.allIncome.length,
      billsCount: result.allBills.length,
      incomeIds: result.allIncome.map((i) => i.id).sort(),
      billsIds: result.allBills.map((b) => b.id).sort(),
    });

    // Skip if no patterns detected or already bridged with same data
    if (
      loading ||
      (result.allIncome.length === 0 && result.allBills.length === 0) ||
      signature === resultSignature.current
    ) {
      return;
    }

    // RECURRING-FIX: Issue 5C - Skip if sync is in progress
    // Wait for sync to complete before bridging to avoid race conditions
    const syncStatus = syncManager.getStatus();
    if (syncStatus.isSyncing) {
      console.log('[useRecurring] Deferring bridge - sync in progress');
      return;
    }

    // Update signature and bridge
    resultSignature.current = signature;

    // Run bridge asynchronously with ALL detected patterns (not sliced)
    bridgeAllDetected(result.allIncome, result.allBills)
      .then(({ incomeCreated, billsCreated }) => {
        if (incomeCreated > 0 || billsCreated > 0) {
          console.log(
            '[useRecurring] Bridged patterns:',
            incomeCreated,
            'income,',
            billsCreated,
            'bills'
          );
        }
      })
      .catch((err) => {
        console.error('[useRecurring] Bridge error:', err);
      });
  }, [loading, result.allIncome, result.allBills]);

  return {
    ...result,
    loading,
  };
}

export default useRecurring;
